/**
 * Phosphor Icons — curated default set
 *
 * Covers common UI, navigation, status, contact, action, and social icons across
 * bold, fill, and regular weights.
 *
 * To add icons in a child theme:
 *   import { library } from '...patterns/01-atoms/svg/phosphor.js';
 *   import MyIconBold from '@phosphor-icons/core/assets/bold/my-icon-bold.svg';
 *   library.add({ bold: { 'my-icon': MyIconBold } });
 *
 * NOTE: Regular weight files have NO weight suffix in the filename:
 *   bold:    assets/bold/house-bold.svg
 *   regular: assets/regular/house.svg   ← no "-regular"
 */

import { library, watch } from './phosphor-library.js';

// ── Bold ─────────────────────────────────────────────────────────────────────

// Navigation
import ArrowRightBold from '@phosphor-icons/core/assets/bold/arrow-right-bold.svg';
import ArrowLeftBold from '@phosphor-icons/core/assets/bold/arrow-left-bold.svg';
import ArrowUpBold from '@phosphor-icons/core/assets/bold/arrow-up-bold.svg';
import ArrowDownBold from '@phosphor-icons/core/assets/bold/arrow-down-bold.svg';
import CaretRightBold from '@phosphor-icons/core/assets/bold/caret-right-bold.svg';
import CaretLeftBold from '@phosphor-icons/core/assets/bold/caret-left-bold.svg';
import CaretUpBold from '@phosphor-icons/core/assets/bold/caret-up-bold.svg';
import CaretDownBold from '@phosphor-icons/core/assets/bold/caret-down-bold.svg';

// UI primitives
import XBold from '@phosphor-icons/core/assets/bold/x-bold.svg';
import ListBold from '@phosphor-icons/core/assets/bold/list-bold.svg';
import MagnifyingGlassBold from '@phosphor-icons/core/assets/bold/magnifying-glass-bold.svg';
import PlusBold from '@phosphor-icons/core/assets/bold/plus-bold.svg';
import MinusBold from '@phosphor-icons/core/assets/bold/minus-bold.svg';
import CheckBold from '@phosphor-icons/core/assets/bold/check-bold.svg';
import DotsThreeBold from '@phosphor-icons/core/assets/bold/dots-three-bold.svg';

// Identity / status
import HouseBold from '@phosphor-icons/core/assets/bold/house-bold.svg';
import UserBold from '@phosphor-icons/core/assets/bold/user-bold.svg';
import BellBold from '@phosphor-icons/core/assets/bold/bell-bold.svg';
import GearBold from '@phosphor-icons/core/assets/bold/gear-bold.svg';
import EyeBold from '@phosphor-icons/core/assets/bold/eye-bold.svg';
import EyeSlashBold from '@phosphor-icons/core/assets/bold/eye-slash-bold.svg';
import LockBold from '@phosphor-icons/core/assets/bold/lock-bold.svg';
import InfoBold from '@phosphor-icons/core/assets/bold/info-bold.svg';
import WarningBold from '@phosphor-icons/core/assets/bold/warning-bold.svg';
import CheckCircleBold from '@phosphor-icons/core/assets/bold/check-circle-bold.svg';
import SpinnerBold from '@phosphor-icons/core/assets/bold/spinner-bold.svg';

// Contact / content
import EnvelopeBold from '@phosphor-icons/core/assets/bold/envelope-bold.svg';
import PhoneBold from '@phosphor-icons/core/assets/bold/phone-bold.svg';
import MapPinBold from '@phosphor-icons/core/assets/bold/map-pin-bold.svg';
import CalendarBold from '@phosphor-icons/core/assets/bold/calendar-bold.svg';
import ClockBold from '@phosphor-icons/core/assets/bold/clock-bold.svg';
import TagBold from '@phosphor-icons/core/assets/bold/tag-bold.svg';
import TrashBold from '@phosphor-icons/core/assets/bold/trash-bold.svg';
import PencilSimpleBold from '@phosphor-icons/core/assets/bold/pencil-simple-bold.svg';
import RssBold from '@phosphor-icons/core/assets/bold/rss-bold.svg';

// Actions
import ShareBold from '@phosphor-icons/core/assets/bold/share-bold.svg';
import DownloadSimpleBold from '@phosphor-icons/core/assets/bold/download-simple-bold.svg';
import UploadSimpleBold from '@phosphor-icons/core/assets/bold/upload-simple-bold.svg';
import ArrowSquareOutBold from '@phosphor-icons/core/assets/bold/arrow-square-out-bold.svg';
import CircleBold from '@phosphor-icons/core/assets/bold/circle-bold.svg';
import BookmarkSimpleBold from '@phosphor-icons/core/assets/bold/bookmark-simple-bold.svg';
import StarBold from '@phosphor-icons/core/assets/bold/star-bold.svg';
import HeartBold from '@phosphor-icons/core/assets/bold/heart-bold.svg';

// Social
import FacebookLogoBold from '@phosphor-icons/core/assets/bold/facebook-logo-bold.svg';
import InstagramLogoBold from '@phosphor-icons/core/assets/bold/instagram-logo-bold.svg';
import XLogoBold from '@phosphor-icons/core/assets/bold/x-logo-bold.svg';
import LinkedinLogoBold from '@phosphor-icons/core/assets/bold/linkedin-logo-bold.svg';
import YoutubeLogoBold from '@phosphor-icons/core/assets/bold/youtube-logo-bold.svg';
import TiktokLogoBold from '@phosphor-icons/core/assets/bold/tiktok-logo-bold.svg';
import PinterestLogoBold from '@phosphor-icons/core/assets/bold/pinterest-logo-bold.svg';
import GithubLogoBold from '@phosphor-icons/core/assets/bold/github-logo-bold.svg';
import WhatsappLogoBold from '@phosphor-icons/core/assets/bold/whatsapp-logo-bold.svg';

// ── Fill ─────────────────────────────────────────────────────────────────────

import StarFill from '@phosphor-icons/core/assets/fill/star-fill.svg';
import HeartFill from '@phosphor-icons/core/assets/fill/heart-fill.svg';
import BellFill from '@phosphor-icons/core/assets/fill/bell-fill.svg';
import EnvelopeFill from '@phosphor-icons/core/assets/fill/envelope-fill.svg';
import CircleFill from '@phosphor-icons/core/assets/fill/circle-fill.svg';
import MapPinFill from '@phosphor-icons/core/assets/fill/map-pin-fill.svg';
import BookmarkSimpleFill from '@phosphor-icons/core/assets/fill/bookmark-simple-fill.svg';
import HouseFill from '@phosphor-icons/core/assets/fill/house-fill.svg';
import EyeFill from '@phosphor-icons/core/assets/fill/eye-fill.svg';
import CheckCircleFill from '@phosphor-icons/core/assets/fill/check-circle-fill.svg';

// ── Regular ──────────────────────────────────────────────────────────────────
// NOTE: Regular weight files have no weight suffix: assets/regular/star.svg

import StarRegular from '@phosphor-icons/core/assets/regular/star.svg';
import HeartRegular from '@phosphor-icons/core/assets/regular/heart.svg';
import BellRegular from '@phosphor-icons/core/assets/regular/bell.svg';
import CircleRegular from '@phosphor-icons/core/assets/regular/circle.svg';
import BookmarkSimpleRegular from '@phosphor-icons/core/assets/regular/bookmark-simple.svg';

// Social
import FacebookLogoRegular from '@phosphor-icons/core/assets/regular/facebook-logo.svg';
import InstagramLogoRegular from '@phosphor-icons/core/assets/regular/instagram-logo.svg';
import XLogoRegular from '@phosphor-icons/core/assets/regular/x-logo.svg';
import TwitterLogoRegular from '@phosphor-icons/core/assets/regular/twitter-logo.svg';
import YoutubeLogoRegular from '@phosphor-icons/core/assets/regular/youtube-logo.svg';
import LinkedinLogoRegular from '@phosphor-icons/core/assets/regular/linkedin-logo.svg';

// ── Register ─────────────────────────────────────────────────────────────────

library.add({
  bold: {
    'arrow-right': ArrowRightBold,
    'arrow-left': ArrowLeftBold,
    'arrow-up': ArrowUpBold,
    'arrow-down': ArrowDownBold,
    'caret-right': CaretRightBold,
    'caret-left': CaretLeftBold,
    'caret-up': CaretUpBold,
    'caret-down': CaretDownBold,
    x: XBold,
    list: ListBold,
    'magnifying-glass': MagnifyingGlassBold,
    plus: PlusBold,
    minus: MinusBold,
    check: CheckBold,
    'dots-three': DotsThreeBold,
    house: HouseBold,
    user: UserBold,
    bell: BellBold,
    gear: GearBold,
    eye: EyeBold,
    'eye-slash': EyeSlashBold,
    lock: LockBold,
    info: InfoBold,
    warning: WarningBold,
    'check-circle': CheckCircleBold,
    spinner: SpinnerBold,
    envelope: EnvelopeBold,
    phone: PhoneBold,
    'map-pin': MapPinBold,
    calendar: CalendarBold,
    clock: ClockBold,
    tag: TagBold,
    trash: TrashBold,
    'pencil-simple': PencilSimpleBold,
    rss: RssBold,
    share: ShareBold,
    'download-simple': DownloadSimpleBold,
    'upload-simple': UploadSimpleBold,
    'arrow-square-out': ArrowSquareOutBold,
    circle: CircleBold,
    'bookmark-simple': BookmarkSimpleBold,
    star: StarBold,
    heart: HeartBold,
    'facebook-logo': FacebookLogoBold,
    'instagram-logo': InstagramLogoBold,
    'x-logo': XLogoBold,
    'linkedin-logo': LinkedinLogoBold,
    'youtube-logo': YoutubeLogoBold,
    'tiktok-logo': TiktokLogoBold,
    'pinterest-logo': PinterestLogoBold,
    'github-logo': GithubLogoBold,
    'whatsapp-logo': WhatsappLogoBold,
  },
  fill: {
    star: StarFill,
    heart: HeartFill,
    bell: BellFill,
    envelope: EnvelopeFill,
    circle: CircleFill,
    'map-pin': MapPinFill,
    'bookmark-simple': BookmarkSimpleFill,
    house: HouseFill,
    eye: EyeFill,
    'check-circle': CheckCircleFill,
  },
  regular: {
    star: StarRegular,
    heart: HeartRegular,
    bell: BellRegular,
    circle: CircleRegular,
    'bookmark-simple': BookmarkSimpleRegular,
    'facebook-logo': FacebookLogoRegular,
    'instagram-logo': InstagramLogoRegular,
    'x-logo': XLogoRegular,
    'twitter-logo': TwitterLogoRegular,
    'youtube-logo': YoutubeLogoRegular,
    'linkedin-logo': LinkedinLogoRegular,
  },
});

export { library, watch };
export default () => watch();
